/**
 * A Scent Journal post, as the admin panel sees it.
 *
 * Two shapes, because the two admin reads return different columns: the list
 * endpoint omits `body` (the whole post, and nothing the table renders), while
 * the single-post endpoint returns the row in full for the editor to load.
 */
export interface BlogPostSummary {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  cover_image: string;
  status: BlogStatus;
  /** Set once, the first time the post is published. Null while it is a draft. */
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BlogPost extends BlogPostSummary {
  /** HTML from the TipTap editor. Rendered as-is on the storefront. */
  body: string;
}

export type BlogStatus = 'draft' | 'published';

/** What the create/update endpoints accept. */
export interface BlogPostPayload {
  title: string;
  slug: string;
  excerpt: string;
  cover_image: string;
  body: string;
  status: BlogStatus;
}

/**
 * Field limits, mirrored from the ecommerce service's routes/blog.routes.ts,
 * which is the one that actually enforces them. This copy exists so the form can
 * count characters and disable Save before a round trip — never as the only
 * check.
 */
export const TITLE_MAX = 120;
export const EXCERPT_MAX = 300;
export const BODY_MIN = 100;
export const SLUG_MAX = 80;

/**
 * Turn a title into a URL segment.
 *
 * Must produce the same result as slugify() in the ecommerce service — the
 * server re-derives the slug from whatever this sends, so a client that
 * disagreed would show the author one URL and save another.
 */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, SLUG_MAX)
    .replace(/^-+|-+$/g, '');
}

/**
 * "3 Sep 2026" — how a published date is printed throughout the panel.
 *
 * SQLite writes "YYYY-MM-DD HH:MM:SS" with no zone marker, and it is UTC. Safari
 * refuses that string outright and Chrome reads it as LOCAL time, so the ' ' is
 * swapped for a 'T' and a 'Z' appended before parsing — without which a post
 * published late in the evening IST shows yesterday's date.
 */
export function formatPublishedDate(value: string | null): string {
  if (!value) return '—';
  const iso = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}
